/**
 * Centralized typed query-key registry. Every TanStack Query call MUST use
 * a key from here, not an inline string array. Benefits:
 *   - invalidate cross-feature without copy-pasting strings
 *   - typecheck mutation onSuccess invalidations
 *   - track query relationships in one place
 *
 * Key-shape decisions:
 *   - Top-level prefix matches feature domain (e.g. 'projects', 'contracts')
 *   - .all is always the bare ['domain'] prefix — used for broad invalidation
 *     and as the cache key for the primary list query when there are no filters
 *   - .detail(id) uses ['domain', id] — keeps TanStack Query's prefix
 *     invalidation working: invalidateQueries({ queryKey: ['projects'] })
 *     invalidates all project keys including detail ones
 *   - Sub-resource keys (e.g. projects.projectContracts) follow
 *     ['projects', projectId, 'contracts'] so they sit under the project
 *     detail prefix and get invalidated when the project is invalidated
 */

// ── ID normalisation ─────────────────────────────────────────────────────────

/**
 * Entity IDs reach the registry as BOTH `number` (from DTO fields like
 * `invoice.projectId`) and `string` (from `useParams()`). TanStack Query matches
 * key segments with strict `===`, so `['projects', 42]` does NOT match
 * `['projects', '42', 'invoices']` — a detail page's invalidation silently missed
 * every tab query on the project page. Normalise ONCE, here, so callers can pass
 * whichever form they happen to hold.
 *
 * `String()` rather than `Number()` because several ids legitimately arrive as
 * non-numeric strings from routing, and `String` is total.
 *
 * NOTE: tokens (`publicView(token)`, `publicshare.*`, booking slugs) are
 * deliberately NOT normalised — they are opaque strings, never numeric IDs.
 */
type EntityId = number | string;

/** `42` and `'42'` collapse to the same key segment. */
const eid = (id: EntityId): string => String(id);

/** Nullable variant — preserves `null` (used by the `byProject(null)` keys). */
const eidOrNull = (id: EntityId | null | undefined): string | null =>
  id === null || id === undefined ? null : String(id);

// ── Filter types ─────────────────────────────────────────────────────────────

export type ProjectListFilters = {
  status?: string;
  clientId?: number;
  type?: string;
};

export type OfferListFilters = {
  projectId?: number;
  status?: string;
};

export type InvoiceListFilters = {
  projectId?: number;
  status?: string;
};

export type ContractListFilters = {
  projectId?: number;
  status?: string;
};

export type TaskListFilters = {
  projectId?: number;
  assigneeId?: number;
  status?: string;
  label?: string;
};

export type ShootListFilters = {
  projectId?: number;
  from?: string;
  to?: string;
};

export type CalendarListFilters = {
  from?: string;
  to?: string;
  projectId?: number;
};

export type InboxListFilters = {
  label?: string;
  projectId?: number;
  unread?: boolean;
};

export type GalleryListFilters = {
  projectId?: number;
};

export type FinanceListFilters = {
  year?: number;
  month?: number;
};

export type TeamListFilters = {
  role?: string;
};

export type QuestionnaireListFilters = {
  projectId?: number;
  status?: string;
};

export type AdminUserListFilters = {
  page?: number;
  search?: string;
};

// ── Registry ─────────────────────────────────────────────────────────────────

export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
    sessions: ['auth', 'sessions'] as const,
    /**
     * Shape contract: `MyStudiosResponse` — `{ current: string | null,
     * studios: { slug, name, roles: string[] }[] }` from `GET /auth/my-studios`.
     * Feeds the in-app studio switcher AND the zero-membership page, which is
     * simply its empty case. Reachable by an identity-only session, so this is
     * one of the few keys that can hold data while `teamMember` is null.
     */
    myStudios: ['auth', 'my-studios'] as const,
  },

  projects: {
    /**
     * Shape contract: `Paged<Project>` — every project the org owns, newest first,
     * assembled by `fetchAllPages('/projects')`. ONLY ProjectsPage uses this as a
     * queryFn source. All other callers that need a project dropdown list must use
     * `projects.minimal` (`/projects/lookup`). Using a different endpoint or
     * returning a derived shape here is a cache collision.
     *
     * The fetch must reach the end: ProjectsPage splits active from terminal
     * projects, counts the status pills and searches — all client-side over this
     * array — so a capped fetch silently caps all four.
     */
    all: ['projects'] as const,
    /** `['projects', id]` — sits under `.all` prefix so broad invalidation works */
    detail: (id: EntityId) => ['projects', eid(id)] as const,
    list: (filters?: ProjectListFilters) =>
      ['projects', 'list', filters ?? null] as const,
    recent: ['projects', 'recent'] as const,
    /**
     * Shape contract: `number` — the ACTIVE project count, i.e. `totalElements` of
     * `GET /projects?excludeStatuses=CLOSED,CANCELLED`. Dashboard "Active Projects" tile.
     */
    count: ['projects', 'count'] as const,
    /** Shape contract: `number` (count) from `GET /projects/count?status=`. Dashboard "new inquiries" banner. */
    statusCount: (status: string) => ['projects', 'count', status] as const,
    myWork: ['projects', 'my-work'] as const,
    /**
     * Shape contract: `InquiryProject[]` — the `content` of `GET /projects?status=INQUIRY`,
     * narrowed client-side to `inquiryConfirmedAt != null`. Backs the Inquiries board's
     * "In progress" column. Kept under the `projects` prefix so project mutations
     * invalidate it along with everything else.
     */
    inquiryInProgress: ['projects', 'inquiry-in-progress'] as const,
    timeline: (id: EntityId) => ['projects', eid(id), 'timeline'] as const,
    activity: (id: EntityId) => ['projects', eid(id), 'activity'] as const,
    /**
     * Shape contract: `ServiceTypeOption[]` from `GET /service-types` — the ACTIVE
     * subset. ProjectsPage, QuickInquiryModal, InquiryFormSection,
     * BookingSettingsSection, InboxPage.
     *
     * This said "the FULL list, active and inactive" until 2026-08-11 and was simply
     * wrong: the endpoint's `active` param defaults to TRUE server-side, so a bare
     * `GET /service-types` has always answered active-only. Every consumer above
     * wants exactly that — they are pickers for new work, and offering a retired
     * type would be a bug. The screen that genuinely needs everything is
     * `allTypes` below.
     */
    types: ['service-types'] as const,
    /**
     * Shape contract: `ServiceTypeOption[]` from `GET /service-types?active=false` —
     * the FULL list, active and hidden. Read `active=false` as "do not filter": the
     * server's param means activeOnly.
     *
     * Sole consumer is Settings → Service types, which is the only screen that may
     * show a hidden type because it is the only one that can un-hide it. It needs
     * its own key: sharing `types` meant whichever mounted first won the cache, so
     * a picker mounting before the settings page would silently hand it the
     * active-only list and hidden types would vanish all over again.
     *
     * Nested under the `service-types` prefix for the same reason as `activeTypes`
     * — an existing non-exact invalidation of `projects.types` still reaches it.
     */
    allTypes: ['service-types', 'all'] as const,
    /**
     * Shape contract: `ServiceTypeOption[]` from `GET /service-types?active=true` — the
     * active subset only. InquiryWebhooksSection's trigger picker, which must never offer an
     * inactive type as a webhook trigger.
     *
     * Deliberately nested under the `service-types` prefix so an existing non-exact
     * invalidation of `projects.types` still reaches it. Splitting it out of that prefix
     * would trade a cache collision for a staleness bug.
     */
    activeTypes: ['service-types', 'active'] as const,
    myProjects: ['my-projects'] as const,
    /**
     * Shape contract: `ProjectDeletePreviewResponse` from `GET /projects/{id}/delete-preview`
     * — `{ projectId, projectTitle, blockers: DeletionBlocker[] }` where each
     * DeletionBlocker is `{ type: 'Offer'|'Contract'|'Invoice', id, title, suggestedAction }`.
     * Surfaces accepted offers/contracts + non-DRAFT invoices for an informed confirm.
     * Fetched on demand (enabled: false) — triggered manually before the delete confirm opens.
     */
    deletePreview: (id: EntityId) => ['projects', eid(id), 'delete-preview'] as const,
    /**
     * Shape contract: `{ content: Array<{id, title, status, ...minimal}>, total_elements: number }`
     * from `GET /projects/lookup`. Used by dialogs and widgets that need a project
     * dropdown list. Do NOT use `projects.all` for this purpose.
     */
    minimal: ['projects-minimal'] as const,
    // Sub-resources — scoped under project id so project-level invalidation clears them
    projectContracts: (id: EntityId) => ['projects', eid(id), 'contracts'] as const,
    projectTeam: (id: EntityId) => ['projects', eid(id), 'team'] as const,
    projectOffers: (id: EntityId) => ['projects', eid(id), 'offers'] as const,
    projectInvoices: (id: EntityId) => ['projects', eid(id), 'invoices'] as const,
    projectExpenses: (id: EntityId) => ['projects', eid(id), 'expenses'] as const,
    projectQuestionnaireResponses: (id: EntityId) =>
      ['projects', eid(id), 'questionnaire-responses'] as const,
    /**
     * GET /api/v1/attention/project/{id} → AttentionPanelResponse:
     * `{ mine: AttentionItemResponse[], studio: AttentionItemResponse[] }` — the SAME
     * shape and the SAME store as `dashboard.attention`, filtered to this project (F200).
     * AttentionItemResponse = `{ id: number; type: string;
     * severity: 'HIGH' | 'MEDIUM' | 'LOW'; message: string; link: string;
     * firstDetectedAt: string; status: 'OPEN' | 'SNOOZED'; snoozedUntil: string | null }`.
     * Server-rendered messages (caller's UI locale); the panel never builds item text.
     */
    projectAttention: (id: EntityId) => ['projects', eid(id), 'attention'] as const,
    projectShoots: (id: EntityId) => ['projects', eid(id), 'shoots'] as const,
    projectGalleries: (id: EntityId) => ['projects', eid(id), 'galleries'] as const,
    projectTasks: (id: EntityId) => ['projects', eid(id), 'tasks'] as const,
    projectEmails: (id: EntityId) => ['project-emails', eid(id)] as const,
    projectCalendar: (id: EntityId) => ['calendar-by-project', eid(id)] as const,
    projectReminders: (id: EntityId, dir: string, status: string | null) =>
      ['project-reminders', eid(id), dir, status] as const,
    /**
     * Shape contract: `ProjectClosingImpactResponse` from
     * `GET /projects/{id}/closing-impact?targetStatus=` — `{ currency: string,
     * unpaidInvoices: Array<{id, type, amount}>, openTasks: Array<{id, title}>,
     * paidDeposits: Array<{id, type, amount}> }`. What a move to CLOSED/CANCELLED would
     * silence; drives the one-time confirmation at the transition (F89). Keyed by target
     * status because paidDeposits is CANCELLED-only.
     */
    closingImpact: (id: EntityId, targetStatus: string | null) =>
      ['project-closing-impact', eid(id), targetStatus] as const,
    clientProjects: (clientId: EntityId) => ['client-projects', eid(clientId)] as const,
  },

  clients: {
    /**
     * Shape contract: `Paged<Client>` — the COMPLETE client roster, newest first,
     * assembled by `fetchAllPages('/clients')`. queryFn must return the raw paged
     * shape. Never return a derived scalar (e.g. totalElements alone) — that breaks
     * every other callsite reading content.
     *
     * ALWAYS READ IT THROUGH `useAllClients()` (features/clients/hooks). The key used
     * to be pinned to size=200 while other callers wanted more, and because the cache
     * is keyed on the KEY and not the queryFn, whichever query mounted first silently
     * decided how much the others could see. One hook removes the disagreement; a
     * caller that needs a plain count uses `clients.count` instead of shrinking this.
     */
    all: ['clients'] as const,
    /**
     * Shape contract: `number` — `totalElements` of `GET /clients?size=1`. The
     * dashboard "Total clients" tile, which wants the number and not the roster.
     * Nested under ['clients'] so every clients.all invalidation refreshes it.
     */
    count: ['clients', 'count'] as const,
    /**
     * Shape contract: `Paged<Client>` from `GET /clients?size=2000` — the full
     * client roster. Used where completeness is load-bearing (duplicate-email
     * detection before creating a client; the backend upserts by email, so a
     * missed duplicate clobbers the existing client's name/phone). Separate key
     * from `.all` on purpose. Nested under ['clients'] so every existing
     * clients.all invalidation still refreshes it.
     */
    lookupAll: ['clients', 'lookup-all'] as const,
    list: (search?: string) => ['clients', 'list', search ?? null] as const,
    detail: (id: EntityId) => ['clients', eid(id)] as const,
    /**
     * Shape contract: `ClientDeletePreviewResponse` from `GET /clients/{id}/delete-preview`.
     * Counts non-deleted items that would be cascade-deleted with this client.
     * Fetched on demand (enabled: false) — triggered manually before the confirm dialog opens.
     */
    deletePreview: (id: EntityId) => ['clients', eid(id), 'delete-preview'] as const,
    /**
     * Shape contract: `ClientBulkDeletePreviewResponse` from `POST /clients/bulk-delete-preview`.
     * Aggregated cascade counts for multiple selected clients. Fetched on demand before confirming.
     */
    bulkDeletePreview: (ids: number[]) => ['clients', 'bulk-delete-preview', ids] as const,
    // Sub-resource list queries used on ClientDetailPage
    projects: (id: EntityId) => ['client-projects', eid(id)] as const,
    invoices: (id: EntityId, extra: string) => ['client-invoices', eid(id), extra] as const,
    offers: (id: EntityId, extra: string) => ['client-offers', eid(id), extra] as const,
    contracts: (id: EntityId, extra: string) => ['client-contracts', eid(id), extra] as const,
  },

  offers: {
    /**
     * Shape contract: `Offer[]` — raw array from `GET /offers`.
     * Filter/sort/group is done client-side in useMemo, never in the queryFn.
     */
    all: ['offers'] as const,
    list: (filters?: OfferListFilters) =>
      ['offers', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['offers', eid(id)] as const,
    activity: (id: EntityId) => ['offers', 'activity', eid(id)] as const,
    publicView: (token: string) => ['offers', 'public', token] as const,
    templates: ['offers', 'templates'] as const,
    template: (id: EntityId) => ['offers', 'templates', eid(id)] as const,
  },

  invoices: {
    /**
     * Shape contract: `Paged<Invoice>` — the COMPLETE invoice list, newest first,
     * assembled by `fetchAllPages('/invoices')`. Read it through `useAllInvoices()`.
     * Filter/sort/status-pill counts are done client-side in useMemo, never in the
     * queryFn — which is exactly why the fetch has to reach the end: capped at one
     * page those counts were the cap, not the totals.
     */
    all: ['invoices'] as const,
    list: (filters?: InvoiceListFilters) =>
      ['invoices', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['invoices', eid(id)] as const,
    /**
     * Under the `invoices` prefix so `invoices.all` invalidation reaches it,
     * matching offers.activity / contracts.activity. It used to be
     * ['invoice-activity', id] — OUTSIDE the prefix — so the invoice's history
     * disclosure never refreshed after a send / mark-paid.
     */
    activity: (id: EntityId) => ['invoices', 'activity', eid(id)] as const,
    artists: ['invoice-artists'] as const,
    financialReport: ['financial-report'] as const,
    /** Shape contract: `InvoiceTemplateResponse[]` from `GET /invoice-templates`. */
    templates: ['invoice-templates'] as const,
    /** Shape contract: `InvoiceTemplateResponse` from `GET /invoice-templates/:id`. */
    template: (id: EntityId) => ['invoice-templates', eid(id)] as const,
  },

  contracts: {
    /**
     * Shape contract: `Contract[]` — raw array from `GET /contracts`.
     * Filter/sort is done client-side in useMemo, never in the queryFn.
     */
    all: ['contracts'] as const,
    list: (filters?: ContractListFilters) =>
      ['contracts', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['contracts', eid(id)] as const,
    activity: (id: EntityId) => ['contracts', 'activity', eid(id)] as const,
    publicView: (token: string) => ['contracts', 'public', token] as const,
    templates: ['contracts', 'templates'] as const,
    template: (id: EntityId) => ['contracts', 'templates', eid(id)] as const,
  },

  tasks: {
    /**
     * Shape contract: `Paged<TaskResponse>` — raw paged response from `GET /tasks?size=200`.
     * NEVER return a derived array (filtered/sliced) from the queryFn — that poisons
     * the cache for every other callsite. All derivation goes in useMemo.
     */
    all: ['tasks'] as const,
    list: (filters?: TaskListFilters) =>
      ['tasks', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['tasks', eid(id)] as const,
    subtasks: (id: EntityId) => ['tasks', eid(id), 'subtasks'] as const,
    labels: ['tasks', 'labels'] as const,
    /**
     * Shape contract: `Paged<TaskResponse>` — raw paged response from
     * `GET /my/tasks?size=2000&excludeStatuses=DONE`, i.e. the ACTIVE half of a Crew
     * member's own board. The done half is a separate endpoint call with different
     * params, so it is a separate key (`myTasksDone`) rather than a second reader of
     * this one — see `api/cacheGroups.ts` for why sibling keys are strangers.
     */
    myTasks: ['tasks', 'my-tasks'] as const,
    /**
     * Shape contract: `Paged<TaskResponse>` — `GET /my/tasks` with
     * `excludeStatuses=[OPEN,IN_PROGRESS]`, loaded only while "Show done tasks" is on.
     * Deliberately nested UNDER `myTasks` so the existing `['tasks']` invalidations
     * (and a `['tasks','my-tasks']` one) reach it by prefix.
     */
    myTasksDone: ['tasks', 'my-tasks', 'done'] as const,
    active: ['tasks', 'active'] as const,
    done: ['tasks', 'done'] as const,
  },

  shoots: {
    /**
     * Shape contract: `ShootListItem[]` (`ShootResponse | BusyShootResponse`) — raw array
     * from `GET /shoots` with caller-specific params. ShootsPage is the primary owner of
     * this key. Dashboard widgets that need upcoming-only shoots must use
     * `shoots.upcomingAttention` instead.
     *
     * IN-114 A1: for a gated (SCOPE_ASSIGNED_ONLY) Crew member, `GET /shoots` (no
     * `projectId`/`teamMemberId`) mixes in redacted `busy:true` rows (id null) for shoots
     * elsewhere in the studio — ShootsPage renders them as a flat "Busy" card.
     */
    all: ['shoots'] as const,
    list: (filters?: ShootListFilters) =>
      ['shoots', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['shoots', eid(id)] as const,
    next: ['shoots-next'] as const,
    nextFiltered: (filterMine: string) => ['shoots-next', filterMine] as const,
    byProject: (projectId: EntityId | null) => ['shoots-by-project', eidOrNull(projectId)] as const,
    /**
     * Shape contract: `Shoot[]` — raw array from `GET /shoots?upcoming=true`. Owned by
     * UpcomingShootsWidget. Slice / filter for display goes in the component's useMemo,
     * never in the queryFn.
     *
     * IN-114 A1: for a gated (SCOPE_ASSIGNED_ONLY) Crew member this array also carries
     * redacted `busy:true` rows for entries elsewhere in the studio — the widget drops
     * them (they are not this viewer's work), never renders them.
     */
    upcomingAttention: ['shoots', 'upcoming-attention'] as const,
  },

  calendar: {
    all: ['calendar'] as const,
    entries: ['calendar-entries'] as const,
    events: (filters?: CalendarListFilters) =>
      ['calendar', 'events', filters ?? null] as const,
    /**
     * SHAPE: a single `CalendarEntryResponse` from `GET /calendar/{id}`.
     * Used by CalendarPage to resolve `?entry=<id>` when the entry falls
     * outside the month range currently fetched — which is the normal case
     * after a Back onto the page, since `range` restarts from today.
     */
    event: (id: EntityId) => ['calendar', 'events', eid(id)] as const,
    googleStatus: ['calendar', 'google-status'] as const,
    syncIssues: ['calendar', 'sync-issues'] as const,
    icalFeed: ['ical-feed-url'] as const,
    /**
     * SHAPE: `CalendarEntryResponse[]` from `GET /calendar?from&to`. CalendarPage's grid.
     *
     * IN-114 A1: for a gated (SCOPE_ASSIGNED_ONLY) Crew member this array also carries
     * redacted `busy:true` rows (id null) for entries elsewhere in the studio — the grid
     * renders them as a flat, non-interactive "Busy" block, never a real entry.
     */
    range: (from: string, to: string) => ['calendar', from, to] as const,
    shootDetail: (id: EntityId | null | undefined) => ['shoot-detail', eidOrNull(id)] as const,
    taskDetail: (id: EntityId | null | undefined) => ['task-detail', eidOrNull(id)] as const,
    byProject: (projectId: EntityId | null | undefined) => ['calendar-by-project', eidOrNull(projectId)] as const,
  },

  inbox: {
    all: ['inbox'] as const,
    list: (filters?: InboxListFilters) =>
      ['inbox', 'list', filters ?? null] as const,
    thread: (id: EntityId) => ['inbox', 'thread', eid(id)] as const,
    threadMessages: (id: EntityId) => ['inbox-thread', eid(id)] as const,
    detail: (id: EntityId) => ['inbox', 'detail', eid(id)] as const,
    unreadCount: ['inbox', 'unread-count'] as const,
    summaryAll: ['inbox-summary'] as const,
    summary: (search: string | null) => ['inbox-summary', search ?? ''] as const,
    emailsAll: ['inbox-emails'] as const,
    /**
     * Shape contract: Spring `Page<InboundEmailResponse>` from
     * `GET /inbox?status&search&page&size&sort&serviceTypeId`. `sort` and
     * `serviceTypeId` are part of the key because both are applied server-side —
     * the list is paged, so they change which rows come back, not just their order.
     */
    emails: (
      tab: string | null,
      search: string,
      page: number,
      sort: 'desc' | 'asc' = 'desc',
      serviceTypeId: EntityId | null = null,
    ) => ['inbox-emails', tab, search, page, sort, eidOrNull(serviceTypeId)] as const,
    filterRules: ['inbox-filter-rules'] as const,
    projectsMinimal: ['projects-minimal'] as const,
    /**
     * SHAPE: `{id, name}[]` — a minimal projection of `GET /team-members?size=100`, under its
     * own key. Derivation is deliberate; see `team.membersList` on the `?? data` rule.
     */
    teamMembersMinimal: ['team-members-minimal'] as const,
    emailSuggestions: (prefix: string) => ['email-suggestions', prefix] as const,
    /**
     * SHAPE: `{ orgCanSendEmail: boolean; canCompose: boolean }` from
     * `GET /inbox/capabilities`. Whether email can actually be sent, and whether
     * THIS caller may compose — the UI hides the New Email affordances on the
     * second. Near-static, so it is cached long; invalidate it after an email
     * settings change.
     */
    capabilities: ['inbox', 'capabilities'] as const,
    /**
     * SHAPE: `{ email: string; displayName: string | null; locale: string | null }` from
     * `GET /inbox/recipient-language?email=`. What language the person in To: reads — their
     * own locale, else the studio's — so the template picker can warn about a language
     * mismatch. `locale` is null when the address is not one of the org's clients.
     */
    recipientLanguage: (email: string) => ['inbox', 'recipient-language', email] as const,
  },

  /**
   * Outbox — the log of outbound email (compose/reply + automation/system sends).
   * list(size):      Spring Page<OutboundEmailResponse> from GET /outbox?page=0&size={size}
   *                  (size grows on "load more"; page pinned to 0 so the window is one query).
   * byProject(id):   OutboundEmailResponse[] from GET /outbox/by-project/{id}
   */
  outbox: {
    all: ['outbox'] as const,
    list: (size: number) => ['outbox', 'list', size] as const,
    byProject: (projectId: EntityId) => ['outbox', 'by-project', eid(projectId)] as const,
    /** Email drafts (GET /email-drafts) — EmailDraftResponse[]. Nested under 'outbox' so
     *  invalidating outbox.all (prefix) refreshes drafts too after a save/send/delete. */
    drafts: ['outbox', 'drafts'] as const,
  },

  chat: {
    all: ['chat'] as const,
    channels: ['chat-channels'] as const,
    channel: (id: EntityId) => ['chat', 'channel', eid(id)] as const,
    channelDetail: (id: EntityId) => ['channel-detail', eid(id)] as const,
    messages: (channelId: EntityId, before?: string) =>
      ['chat', 'messages', eid(channelId), before ?? null] as const,
    channelMessages: (channelId: EntityId) => ['chat-messages', eid(channelId)] as const,
    members: (channelId: EntityId) => ['chat', 'members', eid(channelId)] as const,
    nonContractorMembers: ['team-members-non-contractor'] as const,
    entitySearch: (query: string) => ['chat-entity-search', query] as const,
    /**
     * Shape contract: Spring `Page<ChatSearchResultResponse>` from
     * `GET /chat/search?q={term}&page=0&size={size}`.
     *
     * Org-wide message search across every channel the caller belongs to (the
     * server bounds it by membership + org — see `searchInMemberChannels`), NOT
     * the sidebar's client-side channel-name filter, which queries nothing.
     * `size` grows on "load more" and `page` stays 0, so the visible window is
     * always one request — same shape as `outbox.list`.
     */
    messageSearch: (term: string, size: number) => ['chat-message-search', term, size] as const,
  },

  gallery: {
    all: ['gallery'] as const,
    list: (filters?: GalleryListFilters) =>
      ['gallery', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['gallery', eid(id)] as const,
    /**
     * PREFIX, not a query key anyone fetches directly (RB-10 B3). Before keyset paging,
     * this was the exact key for the one `GET /galleries/{id}/photos` call that returned
     * every photo; now that endpoint is paged (see `photoPages` below) and this array is
     * kept only so `photoFingerprints`, `failedPhotos` and every existing
     * `invalidateQueries(queryKeys.gallery.photos(id))` site keep reaching them —
     * and now also reaches `photoPages` — through TanStack's prefix match. Do not add a
     * `queryFn` against this key.
     */
    photos: (galleryId: EntityId) => ['gallery', eid(galleryId), 'photos'] as const,
    /**
     * Shape contract: `InfiniteData<GalleryPhotoPageResponse, string | null>` — keyset pages
     * of `GET /galleries/{id}/photos?setId=&after=&limit=` (RB-10 B3), READY photos only,
     * ordered by `(sortOrder, id)`. `setId` is part of the key because the server now filters
     * by set: `null` is "All media", `-1`/other sentinel values never reach here (the manage
     * page only ever passes a real set id or `null`). Nested under the `photos` prefix so the
     * existing upload-flow invalidations (`onPhotosStale`, `onPhotoReady`'s fingerprints
     * patch) reach every loaded page without a new invalidation site.
     */
    photoPages: (galleryId: EntityId, setId: EntityId | null) =>
      ['gallery', eid(galleryId), 'photos', 'pages', setId == null ? 'all' : eid(setId)] as const,
    /**
     * Shape contract: `GalleryFavoriteList[]` — `{ id, name, photoCount, createdAt, submitted,
     * photoIds: number[] }[]` from `GET /galleries/{id}/favorite-lists` (D100), submitted list
     * first then newest. Read only by the manage page (header chips + Client selection tab);
     * one endpoint, one key, so it is not a cache group. Nested under `['gallery', id]` so
     * `invalidateGalleryRecord` (reopen, submit, any record change) reaches it by prefix.
     */
    favoriteLists: (galleryId: EntityId) => ['gallery', eid(galleryId), 'favorite-lists'] as const,
    /**
     * Shape contract: `{ id, originalFilename, fileSize }[]` from
     * `GET /galleries/{id}/photos/fingerprints` — READY **and PROCESSING**.
     *
     * Its own key rather than a variant of `photos`: that one is READY-only and
     * carries the full render payload, and the two genuinely answer different
     * questions. This one answers "does the gallery already have this file",
     * which is what a re-dropped folder is matched against after a tab reload.
     * Nested under the `['gallery', id]` prefix so existing non-exact
     * invalidations reach it.
     */
    photoFingerprints: (galleryId: EntityId) => ['gallery', eid(galleryId), 'photos', 'fingerprints'] as const,
    /**
     * Shape contract: `{ items: { photoId, originalFilename, fileSize, reason, createdAt }[],
     * total: number }` from `GET /galleries/{id}/photos/failed` (RB-10 A4, bounded RB-10 C1)
     * — `items` is the OLDEST 50 FAILED photos (server-side `FAILED_PHOTOS_PAGE_SIZE`),
     * `total` is the true count of every FAILED photo in the gallery. Feeds
     * `FailedPhotosNotice` above the manage page's photo grid; every displayed count there
     * (header, Retry all N, Remove all N, the remove-all confirm) reads `total`, never
     * `items.length` — Retry all / Remove all already act server-side on the whole FAILED
     * set regardless of how many rows were listed.
     *
     * Nested under `photos` on purpose, NOT a sibling key: the upload flow's
     * existing `onPhotosStale` callback in `GalleryManagePage` already invalidates
     * `queryKeys.gallery.photos(galleryId)`, and TanStack matches by PREFIX, so
     * this key is refreshed by that same call with no new invalidation site to
     * remember. It joins `photoFingerprints` as the second key living under this
     * prefix for exactly that reason.
     */
    failedPhotos: (galleryId: EntityId) => ['gallery', eid(galleryId), 'photos', 'failed'] as const,
    /**
     * Shape contract: `{ total: number, withoutCaptureTime: number }` from
     * `GET /galleries/{id}/photos/summary?setId=` (RB-10 C1) — READY photos in scope (the
     * whole gallery when `setId` is `null`, that set otherwise). Feeds the "Sort photos…"
     * confirmation dialog's count and its amber "N of M have no capture time" note.
     *
     * Nested under `photos` on purpose, like `photoFingerprints`/`failedPhotos` above: it is
     * read under this ONE key only (never joins `cacheGroups.ts`), but sits under the
     * `photos` prefix so the existing `invalidateQueries(queryKeys.gallery.photos(id))` call
     * the sort mutation already makes (to refresh every loaded page) refreshes this too,
     * with no second invalidation site to remember.
     */
    photoSummary: (galleryId: EntityId, setId: EntityId | null) =>
      ['gallery', eid(galleryId), 'photos', 'summary', setId == null ? 'all' : eid(setId)] as const,
    clientNotes: (galleryId: EntityId) => ['gallery', eid(galleryId), 'client-notes'] as const,
    sets: (galleryId: EntityId) => ['gallery', eid(galleryId), 'sets'] as const,
    /**
     * Shape contract: `GalleryActivity` — from `GET /galleries/{id}/activity`.
     * Summary counters plus the newest `entries` (capped server-side; `truncated`
     * says whether more exist).
     */
    activity: (galleryId: EntityId) => ['gallery', eid(galleryId), 'activity'] as const,
    /**
     * Shape contract: `GalleryArchiveQuote` from `POST /galleries/{id}/archive/quote` (RB-10 D)
     * — read by `GalleryArchiveDialog`. `fingerprint` is a cheap, STABLE summary of the request
     * body, never the body itself: a `SELECT_ALL` selection over a 10,000-photo gallery must not
     * turn a query key into a 10,000-element array. It is scope + setId/filter + each id list's
     * length and a rolling hash — see `fingerprintArchiveRequest` in
     * `features/gallery/lib/galleryArchiveRequest.ts`, the key's only producer. Also folds in
     * `webSized` (IN-186) — a Full-size and a Web-size quote for the identical selection are
     * different server answers and must not share a cache entry.
     */
    archiveQuote: (galleryId: EntityId, fingerprint: string) =>
      ['gallery', eid(galleryId), 'archive', 'quote', fingerprint] as const,
    /**
     * Shape contract: `{ pending: number }` from `GET /galleries/{id}/watermark-regeneration`
     * (RB-10 E1b / RB-11 W7) — how many of this gallery's photos still have a
     * `WATERMARK_REGEN` job queued or running. `WatermarkRegenerationProgress` also writes an
     * extra, LOCAL-ONLY `total` field onto this same cache entry right after the 202 from
     * `POST .../regenerate-watermarks` (see `GalleryManageSettingsPanel`'s save mutation) —
     * the server itself never sends `total`, only `pending`, so every subsequent poll response
     * overwrites the cache with a bare `{ pending }` again; the component carries `total`
     * forward itself once captured rather than expecting the cache to hold it.
     *
     * Nested under the `['gallery', id]` detail prefix on purpose, NOT a sibling top-level key:
     * TanStack matches by prefix, so the existing `invalidateGalleryRecord` helper (which
     * invalidates `gallery.detail`/`gallery.design` via `cacheGroups.ts`'s `galleryDetail`
     * group) reaches this key too, with no second invalidation site to remember. It is read
     * under this ONE key only, so it does not itself join `cacheGroups.ts` — that file is for
     * one endpoint fed to several keys, not for a key that happens to sit under a shared
     * prefix.
     */
    watermarkRegeneration: (galleryId: EntityId) =>
      ['gallery', eid(galleryId), 'watermarkRegeneration'] as const,
    publicView: (token: string) => ['gallery', 'public', token] as const,
    clientFavorites: (token: string) =>
      ['gallery', 'public', token, 'favorites'] as const,
    byProject: (projectId: EntityId | null) =>
      ['galleries-by-project', eidOrNull(projectId)] as const,
    artists: ['gallery-artists'] as const,
    myGalleries: ['my-galleries'] as const,
    teamMembers: ['gallery-team-members'] as const,
    design: (id: EntityId) => ['gallery-design', eid(id)] as const,
    projectsForDialog: ['projects-for-gallery-dialog'] as const,
    /**
     * Shape contract: `GalleryTemplate[]` — full list from `GET /galleries/templates`.
     */
    templates: ['gallery-templates'] as const,
    /** Shape contract: `GalleryTemplate` — single template from `GET /galleries/templates/:id`. */
    template: (id: EntityId) => ['gallery-templates', eid(id)] as const,
  },

  finance: {
    all: ['finance'] as const,
    summary: (filters?: FinanceListFilters) =>
      ['finance', 'summary', filters ?? null] as const,
    income: (filters?: FinanceListFilters) =>
      ['finance', 'income', filters ?? null] as const,
    expenses: (filters?: FinanceListFilters) =>
      ['finance', 'expenses', filters ?? null] as const,
    allExpenses: ['expenses'] as const,
    recurring: ['finance', 'recurring'] as const,
    projection: (year?: number) => ['finance', 'projection', year ?? null] as const,
    ownerEarnings: (filters?: FinanceListFilters) =>
      ['finance', 'owner-earnings', filters ?? null] as const,
    documents: ['documents'] as const,
    report: (from: string, to: string) => ['financial-report', from, to] as const,
    plannedTransfers: ['planned-transfers'] as const,
    billingPartners: ['billing-partners'] as const,
    /**
     * Shape contract: `VatReturnResponse` from `GET /api/v1/finance/vat-return?from=&to=`.
     * Contains `vatReturn` (engine output) and `formLayout` (ordered form lines).
     */
    vatReturn: (from: string, to: string) => ['finance', 'vat-return', from, to] as const,
    /**
     * Shape contract: `VatFlatRateItem[]` from `GET /api/v1/finance/vat-flat-rates`.
     * Elected flat-rate VAT bands (Saldosteuersatz) for the current org.
     */
    vatFlatRates: ['finance', 'vat-flat-rates'] as const,
    /**
     * Shape contract: `VatFilingItem[]` from `GET /api/v1/finance/vat-filings`.
     * Filed VAT periods for the current org, sorted newest first.
     */
    vatFilings: ['finance', 'vat-filings'] as const,
  },

  team: {
    all: ['team'] as const,
    list: (filters?: TeamListFilters) =>
      ['team', 'list', filters ?? null] as const,
    member: (id: EntityId) => ['team', eid(id)] as const,
    invites: ['team', 'invites'] as const,
    members: (contractor: boolean) => ['team-members', contractor] as const,
    /**
     * Shape contract: `Paged<TeamMember>` from `GET /team-members?size=200`.
     *
     * queryFn MUST return the raw paged API response — never a derived array. Five call sites
     * share this key; one of them returned `data.content ?? data` (a bare array), which meant
     * whichever query mounted first won the cache and the other consumers either rendered an
     * empty roster or threw `TypeError: x.map is not a function`.
     *
     * Size is pinned to 200 (the largest any consumer needs) — the sites also disagreed on
     * that, so MyTasksPage silently got 100 whenever another page populated the cache first.
     * A caller needing a different page size must register its own key, not reuse this one
     * with a different queryFn.
     *
     * Enforced by `features/finance/components/__tests__/TeamMembersKeyShape.test.tsx`.
     */
    allMembers: ['team-members'] as const,
    /**
     * SHAPE: `TeamMemberOption[]` — a bare array derived from `GET /team-members?size=200`'s
     * envelope, under its own key so it does NOT collide with `allMembers`' envelope contract.
     * The queryFn must commit to `data?.content ?? []`; a `?? data` fallback makes the cached
     * shape depend on the server's response and is how one key came to serve two shapes.
     *
     * TWO consumers: TasksPage and CalendarPage. Both must derive; TasksPage stored the raw
     * envelope here until the endpoint-keyed scan in `TeamMembersKeyShape.test.tsx`
     * (`noTeamMembersKeyCarriesTwoShapes`) caught it. Navigating Tasks → Calendar within the
     * 60s global staleTime served the envelope to CalendarPage, where `teamMembers.length > 0`
     * was `undefined > 0` and both member-filter blocks silently rendered nothing.
     */
    membersList: ['team-members-list'] as const,
    assignments: (memberId: EntityId | null | undefined) =>
      ['member-assignments', eidOrNull(memberId)] as const,
    /**
     * Shape contract: `{ seatsUsed: number; seatLimit: number | null }` from
     * `GET /team-members/seats`. `seatLimit === null` means the plan has no cap —
     * never compare against a sentinel number.
     *
     * The Team page used `billing.status` for this and 403'd for every Admin (F100).
     * This key is deliberately NOT under `billing`: it carries no billing payload, and
     * its audience is every member rather than the Owner.
     *
     * **It sits under `allMembers`' `['team-members']` prefix on purpose, not by accident
     * of spelling.** Seat usage is a function of the roster — a member added, reactivated or
     * removed changes both — so every roster invalidation should refetch it, and nesting is
     * how TanStack expresses "and this too" without a second list to keep in step. It was
     * true by luck until 2026-09-07, when `invalidateTeamRoster` dropped its separate
     * `seats` invalidation and started relying on the prefix; the reliance is now pinned by
     * `__tests__/queryKeyCacheGroups.test.ts`, so moving this key out from under the prefix
     * fails rather than silently freezing the seat counter. Note it reads a DIFFERENT
     * endpoint (`/team-members/seats`), so it is not a member of the roster cache group —
     * only a prefix child of one.
     */
    seats: ['team-members', 'seats'] as const,
  },

  questionnaires: {
    all: ['questionnaires'] as const,
    list: (filters?: QuestionnaireListFilters) =>
      ['questionnaires', 'list', filters ?? null] as const,
    detail: (id: EntityId) => ['questionnaires', eid(id)] as const,
    response: (id: EntityId) => ['questionnaire-response', eid(id)] as const,
    responses: (id: EntityId) =>
      ['questionnaire-responses', eid(id)] as const,
    allResponses: ['all-questionnaire-responses'] as const,
    responseActivity: (id: EntityId) =>
      ['questionnaire-response-activity', eid(id)] as const,
    publicView: (token: string) => ['questionnaires', 'public', token] as const,
    templates: ['questionnaire-templates'] as const,
    template: (id: EntityId) => ['questionnaire-templates', eid(id)] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    list: ['notifications', 'list'] as const,
    unreadCount: ['notification-unread-count'] as const,
    global: ['global-notifications'] as const,
  },

  billing: {
    all: ['billing'] as const,
    subscription: ['billing', 'subscription'] as const,
    /** GET /billing/plans → PlanPricing (public, Stripe-sourced plan prices). */
    plans: ['billing', 'plans'] as const,
    usage: ['billing', 'usage'] as const,
    portal: ['billing', 'portal'] as const,
    lifecycle: ['organization-lifecycle'] as const,
    status: ['billing-status'] as const,
    invoices: ['billing-invoices'] as const,
    /** GET /billing/connect/status → ConnectAccountStatus (Stripe Connect onboarding state). */
    connectStatus: ['billing', 'connect', 'status'] as const,
    /**
     * GET /billing/pending-checkout → `{ pending: boolean; plan: string | null;
     * interval: 'MONTHLY' | 'ANNUAL' | null }`. A paid plan picked at registration
     * that never reached a completed checkout — drives the dashboard resume banner.
     */
    pendingCheckout: ['billing', 'pending-checkout'] as const,
    /**
     * GET /billing/checkout-status → `{ active: boolean; plan: string | null;
     * interval: 'MONTHLY' | 'ANNUAL' | null }`. Polled by the post-Stripe
     * confirmation page. Keyed by session id so a second checkout in the same tab
     * never reads the previous one's cached "not active yet".
     */
    checkoutCompletion: (sessionId: string | null) =>
      ['billing', 'checkout-status', sessionId ?? 'none'] as const,
  },

  settings: {
    /**
     * Shape contract: `StudioSettings` — **whatever this member is allowed to read of
     * the studio settings**. For an Owner, Admin or Bookkeeper that is the raw body
     * of `GET /settings`; for everyone else it is the strictly smaller body of
     * `GET /settings/presentation` (studio name, currency, locale, country, timezone),
     * whose field names are the same. `useStudioSettings` picks the endpoint; nobody
     * else should.
     *
     * This is the ONE key for that concept, at either width. `shared/lib/formatters.ts`
     * reads it directly out of the cache to resolve the studio locale for currency
     * formatting, so a second cache entry for the same concept (there used to be
     * `['settings','studio']`, used by AutoRepliesSection alone) left formatting stale
     * after a settings PATCH — invalidation only flowed one way.
     */
    all: ['settings'] as const,
    /**
     * GET /api/v1/settings/attention-policies → AttentionPolicyResponse[]:
     * `{ policyKey: string; enabled: boolean; thresholdDays: number;
     * severity: 'HIGH'|'MEDIUM'|'LOW'; group: 'SALES'|'DELIVERY'|'FINANCE'|'INBOX' }[]`.
     */
    attentionPolicies: ['settings', 'attention-policies'] as const,
    emailTemplates: ['email-templates'] as const,
    autoReplies: ['settings', 'auto-replies'] as const,
    autoReplyRules: ['auto-reply-rules'] as const,
    autoReplyLogs: ['auto-reply-logs'] as const,
    autoReplyPending: ['settings', 'autoReplyPending'] as const,
    simpleAutoReply: ['settings', 'simpleAutoReply'] as const,
    /**
     * Shape contract: `SendingDomainState` from `GET /settings/sending-domain` —
     * `{ status: 'NONE'|'NOT_STARTED'|'PENDING'|'VERIFIED'|'FAILED', domain, fromLocalpart,
     * fromAddress, records: DnsRecordView[], verifiedAt, lastCheckedAt }`. Tier 1 own-domain.
     */
    sendingDomain: ['settings', 'sending-domain'] as const,
    webhooks: ['settings', 'webhooks'] as const,
    inquiryWebhooks: ['inquiry-webhooks'] as const,
    customLinks: ['custom-links'] as const,
    /**
     * Shape contract: the RAW paged envelope `{ content: PricingItem[], … }` from
     * `GET /pricing-items?size=200` — the WHOLE catalog, archived rows included, no
     * `active` filter. PricingItem = `{ id, name, description, defaultPrice, unit,
     * serviceTypeId, serviceTypeName, bookableOnline, depositAmount, depositPercentage,
     * sortOrder, active }`.
     *
     * Read by three consumers under this ONE key, byte-for-byte the same request:
     * ServicesAndPricesSection (Settings), OfferNewItemRow and OfferTemplateFormSheet.
     * The two pickers drop archived rows client-side rather than asking the server for a
     * filtered list — a second request shape under the same key is what
     * `queryKeyEndpointCollisions.test.ts` fails on, and archived items must stay
     * renderable on offers that already carry them.
     */
    pricingItems: ['settings', 'pricing-items'] as const,
    /*
     * `settings.bookableOnlineCount` is GONE. It cached a count derived from
     * `GET /pricing-items?bookableOnline=true&active=true`, which meant the Booking
     * panel's "N services bookable online" RESTATED the public page's rule instead of
     * reading it — and drifted from it twice (once missing `active`, once missing
     * "the service type is not archived"). The number now rides on
     * `GET /booking/settings` as `bookableServiceCount`, answered by the same
     * `BookableCatalog` call the page renders from, so it lives under
     * `booking.settings` with the rest of that body. A catalogue edit no longer
     * reaches it by prefix — `ServicesAndPricesSection` invalidates
     * `booking.settings` explicitly, including when a SERVICE TYPE is archived, which
     * the old prefix never covered.
     */
    expenseCategories: ['settings', 'expense-categories'] as const,
    serviceTypes: ['settings', 'service-types'] as const,
    integrations: ['settings', 'integrations'] as const,
    documentHandling: ['settings', 'document-handling'] as const,
    retention: ['settings', 'retention'] as const,
    storageUsage: ['storage-usage'] as const,
    /**
     * Shape contract: the RAW `GET /team-members/me/preferences` body — home page,
     * calendar/tasks defaults, first day of week, date/time/number format, sidebar state,
     * shoot notification settings, `uiLocale` and `studioUiLocale`. Every reader stores the
     * whole body and types its own view of it; nobody derives.
     *
     * **The ONE key for that endpoint, deliberately.** There used to be a second,
     * `auth.preferences` (`['me','preferences']`), read only by `i18n/useUiLocaleSync.ts` —
     * and the language mutation invalidated that one while `shared/lib/formatters.ts`,
     * `shared/lib/weekStart.ts`, `shared/store/themeStore.ts`, `HomeRedirect` and the
     * preferences panel all read this one (IN-02, 2026-09-07). Two entries for one endpoint
     * is a duplicate fetch on a good day and a stale screen on a normal one; see
     * `api/cacheGroups.ts` for why, and `__tests__/queryKeyCacheGroups.test.ts` for the
     * guard that now refuses a third.
     */
    preferences: ['preferences'] as const,
    mfaStatus: ['mfa-status'] as const,
    /**
     * Shape contract: `{ items: BackupFileInfo[], dailyLimit?: number }` from
     * `GET /exports/backups`. `dailyLimit` is not in the body — it is read off the
     * `X-Backup-Daily-Limit` response header, which is how many manual backups a day the
     * server allows, so the settings page can state the cap instead of letting it
     * surprise someone. `undefined` when the server did not publish one.
     */
    backups: ['backups'] as const,
    /**
     * Shape contract: `{ state: 'NONE' | 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED', queuedAhead?, filename?,
     * sizeBytes?, finishedAt?, message? }` from `GET /exports/backup/status` — the latest manual backup job
     * (a background queue, RB-38). `message` is the localized failure sentence on FAILED. Polled while the
     * state is QUEUED or RUNNING.
     */
    backupJob: ['backup-job'] as const,
    /**
     * Shape contract: `{ frequency: 'OFF' | 'WEEKLY' | 'MONTHLY', weekday: 1-7, dayOfMonth: 1-28, hour: 0-23,
     * timezone: 'Europe/Zurich', backupBytes: number }` from `GET /exports/backup/schedule` — the opt-in
     * scheduled backup (off by default) and the bytes this studio's backup files use of its storage.
     */
    backupSchedule: ['backup-schedule'] as const,
    loginMethods: ['me-login-methods'] as const,
    gcalStatus: ['gcal-status'] as const,
    /**
     * Shape contract: `{ id: string, summary: string, primary: boolean }[]` from
     * `GET /auth/google/calendar/connect/calendars` — the writable calendars of the Google account that
     * just signed in (ADR-038 rule 1). Only meaningful while a sign-in is pending; 409 otherwise.
     */
    gcalConnectCalendars: ['gcal-connect-calendars'] as const,
    /**
     * Shape contract: `{ calendarId, calendarSummary, primary, hadCalendarBefore, sameCalendarAsBefore,
     * importCount, pushCount, importFrom, importFromDate, importDaysBack }` from
     * `GET /auth/google/calendar/connect/preview?calendarId=` — the confirmation screen's real counts.
     */
    gcalConnectPreview: (calendarId: string) => ['gcal-connect-preview', calendarId] as const,
    /**
     * Shape contract: `{ entryCount: number, shootCount: number, shoots: { entryId, title, startDateTime }[] }`
     * from `GET /auth/google/calendar/disconnect/preview` — what a disconnect's "delete all" would delete.
     */
    gcalDisconnectPreview: ['gcal-disconnect-preview'] as const,
    deletionStatus: ['deletion-status'] as const,
    mySignature: ['my-signature'] as const,
    meSignatureData: ['me-signature-data'] as const,
    /**
     * SHAPE: `{ content: TeamMemberOption[] }` — the RAW paged envelope from
     * `GET /team-members?size=200`, unwrapped at the consumer (ProjectionSection). One of several
     * deliberate keys on that endpoint; the canonical roster — which keys exist, which contract
     * each holds, and which consumers read them — lives in the `GET /team-members {size:200}`
     * alias note in `__tests__/queryKeyEndpointCollisions.test.ts`. Deliberately not repeated
     * here: a count written in two files is a count that drifts.
     */
    teamMembersForOwnership: ['team-members-for-ownership'] as const,
    ownershipConfig: ['ownership-config'] as const,
    pushSubscriptions: ['push-subscriptions'] as const,
    vapidPublicKey: ['vapid-public-key'] as const,
    recentlyDeletedCount: (admin: boolean) => ['recently-deleted-count', admin] as const,
  },

  /**
   * Automation engine (Automations operational area). Backed by /api/v1/automation/*.
   */
  automations: {
    all: ['automations'] as const,
    /** Shape contract: `AutomationRecipe[]` from `GET /automation/recipes`. */
    recipes: ['automations', 'recipes'] as const,
    /** Shape contract: `AutomationRecipe` from `GET /automation/recipes/{id}`. */
    recipe: (id: EntityId) => ['automations', 'recipes', eid(id)] as const,
    /** Shape contract: `AutomationRun[]` from `GET /automation/runs[?triggerRefId=]`. */
    runs: (triggerRefId?: number) => ['automations', 'runs', triggerRefId ?? null] as const,
    /** Shape contract: `PendingAction[]` from `GET /automation/pending`. */
    pending: ['automations', 'pending'] as const,
    /** Shape contract: `AutomationSettings` ({autoSend}) from `GET /automation/settings`. */
    settings: ['automations', 'settings'] as const,
    /** Shape contract: `NamedRef[]` — id+name only, derived in the queryFn from
     *  `GET /team-members?size=200`. Deliberately a DIFFERENT key from
     *  team.allMembers, whose contract is the full paged TeamMember response. */
    teamMembers: ['automations', 'team-members'] as const,
  },

  activity: {
    my: ['my-activity'] as const,
    team: ['team-activity'] as const,
    recentlyDeleted: ['recently-deleted'] as const,
    deletionBatches: ['deletion-batches'] as const,
    dashboard: ['dashboard'] as const,
    project: (id: EntityId) => ['project-activity', eid(id)] as const,
  },

  admin: {
    all: ['admin'] as const,
    users: (filters?: AdminUserListFilters) =>
      ['admin', 'users', filters ?? null] as const,
    user: (id: EntityId) => ['admin', 'users', eid(id)] as const,
    orgs: (page?: number) => ['admin', 'orgs', page ?? null] as const,
    org: (id: EntityId) => ['admin', 'orgs', eid(id)] as const,
    stats: ['admin', 'stats'] as const,
    metrics: ['admin-metrics'] as const,
    organizations: ['admin-organizations'] as const,
    storageActivity: (orgId: EntityId, days: number) =>
      ['admin', 'storage-activity', eid(orgId), days] as const,
    storageGrants: (orgId: EntityId) =>
      ['admin', 'storage-grants', eid(orgId)] as const,
    orgAssignments: (orgId: EntityId) =>
      ['admin', 'org-assignments', eid(orgId)] as const,
    /** Platform-admin subscription discount coupons. Shape: Coupon[] (features/admin/types.ts). */
    coupons: ['admin-coupons'] as const,
    /** Discount applied to an org's subscription. Shape: OrgDiscount | null (features/admin/types.ts). */
    orgDiscount: (orgId: EntityId) => ['admin', 'org-discount', eid(orgId)] as const,
    /**
     * The signed-in platform admin's own second factors.
     * Shape: AdminFactors (features/admin/types.ts) from GET /admin/security/factors.
     */
    securityFactors: ['admin-security-factors'] as const,
  },

  publicshare: {
    offer: (token: string) => ['publicshare', 'offer', token] as const,
    contract: (token: string) => ['publicshare', 'contract', token] as const,
    gallery: (token: string) => ['publicshare', 'gallery', token] as const,
    questionnaire: (token: string) =>
      ['publicshare', 'questionnaire', token] as const,
    /**
     * GET /public/archive-exports/{exportKey} → PublicArchiveExportView (the public download
     * page, IN-215 D35). Shape contract: `{ galleryName, photoCount, totalBytes, expiresAt,
     * partCount, pendingCount, parts: {partNumber, url, bytes, filename}[], locale }`. The key
     * is a capability: it lives only in this query key and the one request it names.
     */
    archiveExport: (exportKey: string) => ['publicshare', 'archive-export', exportKey] as const,

    /** GET /invoices/pay/{token} → PublicInvoiceView (public pay-by-card page). */
    invoice: (token: string) => ['publicshare', 'invoice', token] as const,

    /** GET /public/inquiry/{slug}/config → InquiryFormConfig (public inquiry form). */
    inquiryConfig: (slug: string) => ['publicshare', 'inquiry-config', slug] as const,

    // ── Client gallery (permitAll) ──────────────────────────────────────────
    // All four families sit under ['publicshare','gallery',slug] so a single
    // prefix clears the whole gallery, but each keeps its OWN sub-prefix so a
    // favourite toggle does not drag the full gallery payload down with it.
    // The *All() variants are the invalidation prefixes.

    /** GET /public/galleries/{slug} → PublicGalleryView. previewMode/pin are part
     *  of the key: a PIN-gated fetch returns different content than an anonymous one. */
    galleryView: (slug: string, previewMode: boolean, pin: string | null) =>
      ['publicshare', 'gallery', slug, 'view', previewMode, pin ?? null] as const,
    galleryViewAll: (slug: string) => ['publicshare', 'gallery', slug, 'view'] as const,

    /**
     * Shape contract: `InfiniteData<PublicGalleryPhotoPageResponse, string | null>` — keyset
     * pages of the client-facing photo listing (RB-10 B3): `GET /public/galleries/{slug}/photos`
     * (or the staff-preview twin `GET /galleries/preview/{slug}/photos`), READY only. The
     * manifest (`galleryView`) no longer carries the photo list itself, only
     * `photoCount`/`videoCount`.
     *
     * `previewMode` and `pin` are part of the key for the same reason `galleryView` carries
     * them: a PIN-gated or staff-preview fetch returns different content than an anonymous
     * one.
     *
     * The key's LAST element is the scope, one of three encodings (RB-10 C3 — `setId` and
     * `favoriteListId` are mutually exclusive, same as the server's own 400 on both together):
     *   - `'all'` — no `setId`, no `favoriteListId`.
     *   - `eid(setId)` (a bare stringified set id) — the "All" tab filtered to one set.
     *   - `` `fav-${eid(favoriteListId)}` `` — the Favourites tab, server-filtered to that
     *     list (RB-10 C3 — this used to be a client-side filter over the 'all' scope's loaded
     *     pages; the server now pages the list directly).
     * `favoriteListId` wins when both are supplied — callers should never do that (the server
     * 400s it), but the key still has to pick one shape deterministically.
     */
    galleryPhotoPages: (
      slug: string,
      previewMode: boolean,
      pin: string | null,
      setId: EntityId | null,
      favoriteListId?: EntityId | null,
    ) =>
      [
        'publicshare', 'gallery', slug, 'photos', previewMode, pin ?? null,
        favoriteListId != null ? `fav-${eid(favoriteListId)}` : setId == null ? 'all' : eid(setId),
      ] as const,

    /** GET /public/galleries/{slug}/favorite-lists → FavoriteList[]. Every list on the
     *  gallery, shared by everyone holding the link — NOT the per-token
     *  /my-favorite-lists, which made a second device look locked out of a selection
     *  it was always allowed to see and edit. No client token in the key: the response
     *  does not vary by device. */
    galleryFavoriteLists: (slug: string, galleryId: EntityId | null | undefined) =>
      ['publicshare', 'gallery', slug, 'fav-lists', eidOrNull(galleryId)] as const,
    galleryFavoriteListsAll: (slug: string) =>
      ['publicshare', 'gallery', slug, 'fav-lists'] as const,

    /** GET /public/galleries/{slug}/favorite-lists/{listId}/photos → number[]. */
    galleryFavorites: (slug: string, listId: EntityId | null | undefined) =>
      ['publicshare', 'gallery', slug, 'favorites', eidOrNull(listId)] as const,
    galleryFavoritesAll: (slug: string) =>
      ['publicshare', 'gallery', slug, 'favorites'] as const,

    /** GET /public/galleries/{slug}/notes → ClientNote[]. */
    galleryNotes: (slug: string, galleryId: EntityId | null | undefined) =>
      ['publicshare', 'gallery', slug, 'notes', eidOrNull(galleryId)] as const,
    galleryNotesAll: (slug: string) => ['publicshare', 'gallery', slug, 'notes'] as const,

    /** GET /contracts/{id}/admin-preview — studio-side preview of the public view. */
    contractAdminPreview: (id: EntityId) =>
      ['publicshare', 'contract-admin-preview', eid(id)] as const,
    /** GET /offers/{id}/admin-preview. */
    offerAdminPreview: (id: EntityId) =>
      ['publicshare', 'offer-admin-preview', eid(id)] as const,
    /**
     * GET /questionnaire-responses/{id}/admin-preview → PublicQuestionnaireView.
     * By id, not by token: the raw submit token is transient (bughunt M21), so previewing
     * through a public link would mean minting one and killing the client's current link.
     */
    questionnaireAdminPreview: (id: EntityId) =>
      ['publicshare', 'questionnaire-admin-preview', eid(id)] as const,

    /**
     * Shape contract: `ArchiveQuoteResponse` (RB-10 D1, IN-226 D45) —
     * `GET /public/galleries/{slug}/archive/quote`. ONE answer for every scope (All, each set,
     * Favorites), so no set id is part of the key. Read by `GalleryDownloadDialog` to list the
     * selection rows with their counts/bytes and decide DIRECT vs. export before the client
     * commits to `POST /{slug}/archive`.
     *
     * Deliberately keyed on a `pinPresent` BOOLEAN rather than the raw download PIN value —
     * unlike `galleryPhotoPages`/`galleryView` above, which key on the PIN itself. Those key
     * a whole payload that differs shape entirely between anonymous/PIN-gated; a wrong-vs-right
     * PIN guess here would otherwise mint one cache entry per guess for no benefit, and the
     * quote is re-fetched on every dialog open regardless.
     */
    archiveQuote: (
      slug: string,
      opts: {
        favoriteListId: EntityId | null
        webSized: boolean
        pinPresent: boolean
      },
    ) =>
      [
        'publicshare', 'gallery', slug, 'archive-quote',
        eidOrNull(opts.favoriteListId), opts.webSized, opts.pinPresent,
      ] as const,

  },

  dashboard: {
    /**
     * GET /api/v1/attention → AttentionPanelResponse:
     * `{ mine: AttentionItemResponse[], studio: AttentionItemResponse[] }` where
     * AttentionItemResponse = `{ id: number; type: string;
     * severity: 'HIGH' | 'MEDIUM' | 'LOW'; message: string; link: string;
     * firstDetectedAt: string; status: 'OPEN' | 'SNOOZED'; snoozedUntil: string | null }`.
     * Server-rendered messages (caller's UI locale); the widget never builds item text.
     */
    attention: ['dashboard', 'attention'] as const,
  },

  booking: {
    all: ['booking'] as const,
    /*
     * `booking.services` is GONE (IN-102). `/booking/services` was a second catalog of
     * priced services beside the price list, with the same shape and no link between
     * them; both are now `pricing-items`, and what the public booking page lists is the
     * `bookableOnline` subset — see `settings.bookableOnlineItems`.
     */
    /** Shape contract: `WorkingHoursEntry[]` from `GET /booking/working-hours`. */
    workingHours: ['booking', 'working-hours'] as const,
    /** Shape contract: `BlackoutDate[]` from `GET /booking/blackout-dates`. */
    blackoutDates: ['booking', 'blackout-dates'] as const,
    /**
     * Shape contract: `BookingSettings` from `GET /booking/settings` — including
     * `bookableServiceCount`, how many services the PUBLIC page would offer right now.
     * That number is a function of the CATALOGUE, so `ServicesAndPricesSection`
     * invalidates this key on every item edit and on every service-type edit; see the
     * note where `settings.bookableOnlineCount` used to be.
     */
    settings: ['booking', 'settings'] as const,
    /** Shape contract: `BookableMember[]` from `GET /booking/members`. */
    members: ['booking', 'members'] as const,
    /** Shape contract: `BookingRequest[]` from `GET /booking/requests?status=`. */
    requests: (status?: string) => ['booking', 'requests', status ?? null] as const,
    /** Shape contract: `number` (PENDING count) from `GET /booking/requests/count` ({count}). Backs the nav badge. */
    pendingCount: ['booking', 'requests', 'count'] as const,
    /** Shape contract: `number` from `GET /booking/requests/form-inquiry-count` ({count}). Unconfirmed form/ingest inquiries not linked to a pending booking request. Backs dashboard banner Row 2 + nav badge. */
    formInquiryCount: ['booking', 'requests', 'form-inquiry-count'] as const,
    /** Shape contract: `FormInquiry[]` from `GET /booking/requests/form-inquiries`. Unconfirmed form/ingest inquiries (INQUIRY projects, newest first) not linked to a pending booking request. Backs the Inquiries "New" tab. */
    formInquiries: ['booking', 'requests', 'form-inquiries'] as const,
    /** Shape contract: `InquiryHistoryItem[]` from `GET /booking/requests/inquiry-history`. Settled form/ingest inquiries (confirmed-and-moved-on + dismissed). Backs the Inquiries "History" section alongside non-PENDING booking requests. */
    inquiryHistory: ['booking', 'requests', 'inquiry-history'] as const,
    /** Shape contract: `BookingRequest` from `GET /booking/requests/:id`. */
    request: (id: EntityId) => ['booking', 'requests', eid(id)] as const,
    /** Shape contract: `BookingRequest | null` from `GET /booking/requests/by-project/:projectId` (204 → null). Backs the project detail page's "what the client requested" panel. */
    requestByProject: (projectId: EntityId) =>
      ['booking', 'requests', 'by-project', eid(projectId)] as const,
    /** Shape contract: `AssignableMember[]` from `GET /booking/requests/:id/assignees`. */
    assignees: (id: EntityId) => ['booking', 'requests', eid(id), 'assignees'] as const,
    // ── Public (no-auth) booking page keys ──
    /** Shape contract: `PublicBookingConfig` from `GET /public/booking/:slug/config`. */
    publicConfig: (slug: string) => ['booking', 'public', 'config', slug] as const,
    /** Shape contract: `AvailabilitySlot[]` from `GET /public/booking/:slug/availability`.
     *  `durationMinutes` (null = service default) is part of the key so changing the
     *  client-selected duration refetches the grid. */
    publicAvailability: (slug: string, serviceId: EntityId | null, from: string, to: string,
                         durationMinutes: number | null = null, memberId: EntityId | null = null) =>
      ['booking', 'public', 'availability', slug, eidOrNull(serviceId), from, to,
       durationMinutes, eidOrNull(memberId)] as const,
  },
};
